import * as THREE from 'three';
import type { System } from '../../../systems';
import type { WaterSpec } from '../BiomeProfile';

/**
 * Beautiful liquid surface for planet interiors. A circular plane mesh
 * with a custom ShaderMaterial that:
 *
 *   - displaces vertices with stacked sine waves (subtle, no spikes)
 *   - blends `surfaceTint` with `depthTint` based on view-fresnel so the
 *     edge reads as light water and the deep view reads as colour
 *   - adds animated foam highlights on wave crests
 *   - smoothly fades alpha at the disc rim so the water never has a hard
 *     circular cut against the fog
 *   - supports a "frozen" mode (Solenne, Nyara) — same shader but the
 *     specular flash is sharper and waves slow down
 *
 * The water is not a physics surface; the player walks past it on the
 * dry terrain side. Reads as fully decorative, but it is one of the most
 * effective visual upgrades in the biome system.
 */

export class Water implements System {
  readonly id = 'biome-water';
  readonly object3D: THREE.Mesh;
  private readonly material: THREE.ShaderMaterial;
  private readonly surfaceColor: THREE.Color;
  private readonly depthColor: THREE.Color;
  private readonly foamColor: THREE.Color;
  private readonly emissiveColor: THREE.Color;
  private readonly emissiveStrength: number;
  private readonly waveSpeed: number;
  private time = 0;

  constructor(
    spec: WaterSpec,
    radius: number
  ) {
    const segs = 96;
    const geom = new THREE.CircleGeometry(radius, segs);
    geom.rotateX(-Math.PI / 2);

    this.surfaceColor = new THREE.Color(spec.surfaceTint);
    this.depthColor = new THREE.Color(spec.depthTint);
    this.foamColor = new THREE.Color(spec.foamTint);
    this.emissiveColor = new THREE.Color(spec.emissive ?? spec.surfaceTint);
    this.emissiveStrength = spec.emissiveStrength ?? 0;
    this.waveSpeed = spec.waveSpeed;

    this.material = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      uniforms: {
        uTime: { value: 0 },
        uSurface: { value: this.surfaceColor },
        uDepth: { value: this.depthColor },
        uFoam: { value: this.foamColor },
        uEmissive: { value: this.emissiveColor },
        uEmissiveStrength: { value: this.emissiveStrength },
        uAmplitude: { value: spec.waveAmplitude },
        uIce: { value: spec.ice ? 1 : 0 },
        uRadius: { value: radius },
        uFadeStart: { value: radius * 0.78 }
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
      toneMapped: true
    });

    this.object3D = new THREE.Mesh(geom, this.material);
    this.object3D.position.y = spec.level;
    this.object3D.name = 'biome-water';
    this.object3D.renderOrder = -1;
    this.object3D.frustumCulled = false;
  }

  update(dt: number): void {
    this.time += dt * this.waveSpeed;
    this.material.uniforms['uTime']!.value = this.time;
  }

  dispose(): void {
    this.object3D.geometry.dispose();
    this.material.dispose();
  }
}

const VERT = /* glsl */ `
  uniform float uTime;
  uniform float uAmplitude;

  varying vec3 vWorldPos;
  varying vec3 vNormal;
  varying float vWave;
  varying float vRadial;

  // Stacked sine waves — directionally varied so the surface never reads
  // as a single repeating pattern.
  float wave(vec2 p) {
    float w = 0.0;
    w += sin(p.x * 0.18 + uTime * 1.4) * 0.5;
    w += sin(p.y * 0.21 + uTime * 1.1 + 1.7) * 0.4;
    w += sin((p.x + p.y) * 0.13 + uTime * 0.6 + 2.4) * 0.3;
    w += sin((p.x - p.y) * 0.27 - uTime * 0.9) * 0.18;
    return w;
  }

  void main() {
    vec3 p = position;
    float w = wave(p.xz);
    p.y += w * uAmplitude;
    vWave = w;
    vRadial = length(p.xz);

    // Approximate normal via finite difference in XZ.
    float wx = wave(p.xz + vec2(0.6, 0.0)) - wave(p.xz - vec2(0.6, 0.0));
    float wz = wave(p.xz + vec2(0.0, 0.6)) - wave(p.xz - vec2(0.0, 0.6));
    vec3 n = normalize(vec3(-wx * uAmplitude, 1.0, -wz * uAmplitude));
    vNormal = n;

    vec4 wp = modelMatrix * vec4(p, 1.0);
    vWorldPos = wp.xyz;
    gl_Position = projectionMatrix * viewMatrix * wp;
  }
`;

const FRAG = /* glsl */ `
  uniform vec3 uSurface;
  uniform vec3 uDepth;
  uniform vec3 uFoam;
  uniform vec3 uEmissive;
  uniform float uEmissiveStrength;
  uniform float uIce;
  uniform float uRadius;
  uniform float uFadeStart;
  uniform float uTime;

  varying vec3 vWorldPos;
  varying vec3 vNormal;
  varying float vWave;
  varying float vRadial;

  void main() {
    vec3 N = normalize(vNormal);
    vec3 V = normalize(cameraPosition - vWorldPos);
    float ndv = max(dot(N, V), 0.0);

    // Fresnel — strong rim, fades to depth tint head-on.
    float fres = pow(1.0 - ndv, 3.0);

    vec3 col = mix(uDepth, uSurface, ndv * 0.85 + 0.15);
    col = mix(col, uSurface, fres * 0.6);

    // Foam on wave peaks.
    float foamMask = smoothstep(0.45, 1.1, vWave);
    col = mix(col, uFoam, foamMask * 0.9);

    // Specular highlight — sharper for ice, broader for water.
    float specPow = mix(40.0, 220.0, uIce);
    vec3 sun = normalize(vec3(0.4, 0.85, 0.3));
    vec3 H = normalize(sun + V);
    float spec = pow(max(dot(N, H), 0.0), specPow);
    col += uFoam * spec * mix(0.55, 1.1, uIce);

    // Emissive (luminescent pools).
    col += uEmissive * uEmissiveStrength * (0.6 + foamMask * 0.6);

    // Soft alpha at edges — disc fades smoothly into terrain rather than
    // showing a hard circular cut.
    float edge = smoothstep(uRadius, uFadeStart, vRadial);
    float baseAlpha = mix(0.78, 0.92, uIce);
    float alpha = baseAlpha * edge;

    gl_FragColor = vec4(col, alpha);
  }
`;
